// Local-date helpers. `new Date().toISOString().slice(0, 10)` returns the UTC
// date, which between 00:00 and 04:00 Dubai time gives yesterday's date — wrong
// for things like a date input default that should mean "today, where the user
// is standing." Build the date from local components instead.

export function todayLocalIsoDate(): string {
  return localIsoDate(new Date());
}

export function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const MONTH_REGEX = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * Calendar-month bounds in Dubai-local time (Asia/Dubai = UTC+04:00, no DST),
 * expressed as UTC ISO strings for SQL filtering: occurred_at >= startIso
 * AND occurred_at < endIso.
 *
 * Throws on malformed month input. Caller is responsible for the
 * `month > current_local_month` check; this helper just computes bounds.
 */
export function monthBoundsLocal(month: string): { startIso: string; endIso: string } {
  const m = MONTH_REGEX.exec(month);
  if (!m) throw new Error(`invalid month: ${month}`);
  const year = Number(m[1]);
  const monthIdx = Number(m[2]) - 1;
  const startUtcMs = Date.UTC(year, monthIdx, 1) - 4 * 60 * 60 * 1000;
  const endUtcMs = Date.UTC(year, monthIdx + 1, 1) - 4 * 60 * 60 * 1000;
  return {
    startIso: new Date(startUtcMs).toISOString(),
    endIso: new Date(endUtcMs).toISOString(),
  };
}

/**
 * Format a UTC ISO datetime string as a Dubai-local YYYY-MM-DD.
 * Asia/Dubai = UTC+04:00, no DST. Used by reports drill-down so that
 * a transaction logged just past 20:00 UTC on the last day of a
 * Dubai-local month shows up under the correct day in the sheet.
 */
export function dubaiLocalDay(occurredAtIso: string): string {
  const utcMs = new Date(occurredAtIso).getTime();
  const dubaiMs = utcMs + 4 * 60 * 60 * 1000;
  return new Date(dubaiMs).toISOString().slice(0, 10);
}

/** Add n calendar days to a YYYY-MM-DD string. Uses UTC math to avoid DST/local skew. */
export function addLocalDays(iso: string, n: number): string {
  const parts = iso.split('-').map(Number);
  const y = parts[0]!;
  const m = parts[1]!;
  const d = parts[2]!;
  const base = new Date(Date.UTC(y, m - 1, d));
  base.setUTCDate(base.getUTCDate() + n);
  const yy = base.getUTCFullYear();
  const mm = String(base.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(base.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
